import type { Variants } from 'framer-motion';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { ACCENT_BLUE, EASE_OUT, MUTED_GRAY, NOPING_GREEN, TEXT_LIGHT } from './constants';

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: EASE_OUT as unknown as [number, number, number, number] },
  },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: EASE_OUT as unknown as [number, number, number, number] },
  },
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

export function Section({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={`px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className='mx-auto w-full max-w-6xl'>{children}</div>
    </section>
  );
}

export function SectionTitle({ subtitle, title, titleAccent, description }: { subtitle?: string; title: string; titleAccent?: string; description?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT as unknown as [number, number, number, number] }} className='mb-14 text-center'>
      {subtitle && (
        <p className='mb-4 text-xs font-semibold uppercase tracking-[0.25em]' style={{ color: ACCENT_BLUE }}>
          {subtitle}
        </p>
      )}
      <h2 className='mb-4 text-3xl font-bold sm:text-4xl md:text-5xl' style={{ fontFamily: 'var(--font-bai), sans-serif', color: TEXT_LIGHT }}>
        {title}
        {titleAccent && (
          <>
            {' '}
            <span style={{ color: NOPING_GREEN }}>{titleAccent}</span>
          </>
        )}
      </h2>
      {description && (
        <p className='mx-auto max-w-xl text-lg' style={{ color: MUTED_GRAY }}>
          {description}
        </p>
      )}
    </motion.div>
  );
}

export function RevealOnScroll({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.6, delay, ease: EASE_OUT as unknown as [number, number, number, number] }} className={className}>
      {children}
    </motion.div>
  );
}
